import { captureException } from '@sentry/browser';
import axios from 'axios';

/**
 * Single source of truth for reporting errors to console and Sentry.
 *
 * - Logs all errors to console
 * - Skips cancellations (axios cancel, AbortError)
 * - Prevents double-reporting (interceptor + catch block on same error)
 * - Enriches Axios errors with API context (fingerprint, tags, response data)
 * - Sends all other errors to Sentry as-is
 *
 * Called from:
 * - The Axios response interceptor (apis/axios.js) as a safety net for all API errors
 * - Individual catch blocks via reportError(e) for explicit error handling
 *
 * Use this instead of console.error(e) or captureException(e) in catch blocks.
 *
 * @param {Error} error - The error to report
 * @param {object} [context] - Optional Sentry context (tags, extra, fingerprint, etc.)
 */
export function reportError(error, context = {}) {
  console.error(error);

  // Skip cancellations entirely
  if (axios.isCancel(error) || error?.name === 'AbortError') return;

  // Prevent double-reporting when both the interceptor and a catch block
  // call reportError on the same error object
  if (error._sentryReported) return;
  error._sentryReported = true;

  // Enrich Axios errors with API context for better grouping and debugging
  if (axios.isAxiosError(error)) {
    const status = error.response?.status || 0;
    const url = error.config?.url || 'unknown';

    // Skip 400s — expected validation responses (e.g. create-account form errors)
    // Skip 401s — auth redirects, not real errors
    // NOT skipping 500s — backend rescue_from captures these with real stack traces,
    // so the frontend events are duplicative and periodically ignored in Sentry cleanup.
    // But we still report them as a safety net in case backend coverage has gaps.
    if (status === 400 || status === 401) return;
    // Skip 403 only on /api/users/me — expired session check, not a permission bug
    if (status === 403 && url.includes('/api/users/me')) return;
    // Normalize URL: strip MongoDB IDs and query params for consistent grouping
    const normalizedUrl = url
      .replace(/[a-f0-9]{24}/g, ':id')
      .replace(/\?.*$/, '');

    // Wrap with descriptive message so Sentry issue titles are distinguishable
    // (e.g. "Network Error — GET /api/profiles/:id" instead of just "Network Error")
    const method = (error.config?.method || 'unknown').toUpperCase();
    const sentryError = new Error(
      `${error.message} — ${method} ${normalizedUrl}${status ? ` [${status}]` : ''}`
    );
    sentryError.name = error.name || 'AxiosError';
    sentryError.stack = error.stack;

    captureException(sentryError, {
      fingerprint: [
        'axios-error',
        method.toLowerCase(),
        normalizedUrl,
        String(status || 'unknown'),
      ],
      tags: {
        api_status: status || undefined,
        api_url: error.config?.url,
        api_method: error.config?.method,
        ...(context.tags || {}),
      },
      extra: {
        response_data: error.response?.data,
        ...(context.extra || {}),
      },
    });
    return;
  }

  // Non-Axios errors: send to Sentry with any provided context
  captureException(error, context);
}
